/** Who bills what: vendors, supply accounts, and the meters beneath them — **ADR-0062**.
 *
 * The census compares what arrived against what was expected, and expectation used to be declared
 * per meter. A customer with forty meters under one contract restated forty rows or let the census
 * be wrong. An account is the one row that ends them: retiring it stops every stream on its meters
 * being expected from that date, and the census says *which act* did it
 * (`life_to_basis: "supply_account"`).
 *
 * Four decisions the screen carries and a refactor would remove without looking like it.
 *
 * **Not recorded is not "no supplier".** A meter nobody has attached to an account is a fact about
 * what somebody has told the product. Every meter that existed before this register did is in that
 * state, correctly, so the list of them is its own heading and its sentence says what it does and
 * does not claim — design principle 5, *an absence is said, never left blank*.
 *
 * **The meters nobody has accounted for are listed, not counted.** A screen that listed accounts
 * and left the rest off would read as complete while most of the meters are not on it.
 *
 * **An account's start date narrows every meter on it**, so the form says so beside the field. A
 * customer who moves forty meters onto a successor account and dates it from the changeover would
 * otherwise read the earlier months as never expected, and nothing on the screen would have said
 * why the missing count fell.
 *
 * **A control a role cannot use is not rendered** (design rule 6). The server requires an Approver
 * for every write here, because each one moves the completeness figure; the screen says who can.
 *
 * Nothing here looks a vendor up by what a document says. A vendor is chosen from a list by a
 * person (SEC-05).
 */

import { useContext, useState } from "react";

import type { RegisterMeter, SourceCoverage, SupplyAccount } from "../api/sources";
import {
  useAttachMeters,
  useRecordAccount,
  useRecordVendor,
  useRegisters,
  useRetireAccount,
  useSetMeterAccount,
} from "../api/sources";
import { isPeriodClosed } from "../api/refusal";
import { Loaded, WriteFailed } from "../components/Loaded";
import { PostCloseRetry } from "./PeriodLock";
import { SourcesNavContext } from "./sourceModel";
import { Panel } from "../components/Panel";
import { shortDate } from "../lib/format";
import styles from "./Evidence.module.css";
import own from "./Sources.module.css";
import reg from "./Register.module.css";

/** A meter, written once: its own label where it has one, then where it is. */
export function meterName(meter: RegisterMeter): string {
  return `${meter.label ?? meter.kind.replace(/_/g, " ")} · ${meter.facility_name}`;
}

/** An account's life, in words. Both ends are optional, and *no dates* is a statement. */
export function lifeOf(account: SupplyAccount): string {
  if (account.operational_to) {
    return account.operational_from
      ? `${shortDate(account.operational_from)} to ${shortDate(account.operational_to)}`
      : `until ${shortDate(account.operational_to)}`;
  }
  return account.operational_from ? `from ${shortDate(account.operational_from)}` : "no dates set";
}

/** The census streams that read one meter, by identity (`meter_id`), never by label. */
function streamsOf(streams: readonly SourceCoverage[], meterId: string): SourceCoverage[] {
  return streams.filter((stream) => stream.meter_id === meterId);
}

/** From a meter to the streams that report it (EXP-06): the register's way into the census, and
 * from there into the readings, the evidence, the connections and the workflow. A meter no stream
 * reads says so - that is a gap the census cannot see, and silence would read as nothing to find. */
function MeterStreams({
  meterId,
  streams,
}: {
  meterId: string;
  streams: readonly SourceCoverage[];
}) {
  const nav = useContext(SourcesNavContext);
  const [missing, setMissing] = useState(false);
  const found = streamsOf(streams, meterId);
  if (found.length === 0) {
    return (
      <span className={own.formNote} data-testid={`meter-no-stream-${meterId}`}>
        {" "}
        · no stream expects this meter
      </span>
    );
  }
  return (
    <>
      {found.map((stream) => (
        <span key={stream.source_id}>
          {" "}
          <button
            type="button"
            className={own.ownerAction}
            data-testid={`meter-stream-${stream.source_id}`}
            onClick={() => {
              // With the screen's tabs the stream's row is on another tab, so the screen moves
              // there and answers whether the row is drawn at all; without it (a register drawn on
              // its own) the row is looked for where it would be.
              if (nav) return setMissing(!nav.openStream(stream.source_id));
              const el = document.getElementById(`source-${stream.source_id}`);
              if (!el) return setMissing(true);
              setMissing(false);
              el.scrollIntoView?.({ block: "center" });
              el.focus();
            }}
          >
            Its {stream.category.replace(/_/g, " ")} stream
          </button>
        </span>
      ))}
      {missing ? (
        <span className={own.formNote} role="status">
          {" "}
          That stream is not in the rows currently listed - show all streams above.
        </span>
      ) : null}
    </>
  );
}

export function SourceRegisters({
  mayDeclare,
  streams = [],
}: {
  mayDeclare: boolean;
  streams?: readonly SourceCoverage[];
}) {
  const registers = useRegisters();

  return (
    <Panel
      title="Who bills what"
      sub="Vendors, accounts, and the meters under them"
      column="1 / 13"
      flush
    >
      <div className={own.pad}>
      <p className={own.formNote} data-testid="registers-note">
        An account is what a group of meters is billed under. Ending an account once stops every
        stream on its meters being expected from that date — one row, not forty — and the expected-bills check
        says that the account did it. A meter with no account is <strong>not recorded</strong>:
        nobody has said who bills it, which is not the same as nobody billing it.
      </p>
      </div>
      <Loaded query={registers} of="The register of who bills what">
        {(data) => (
          <>
            <div className={own.pad}>
            <p className={own.verdict} data-testid="registers-count">
              {data.accounts.length} account{data.accounts.length === 1 ? "" : "s"} under{" "}
              {data.vendors.length} vendor{data.vendors.length === 1 ? "" : "s"}.{" "}
              {data.not_recorded.length > 0 ? (
                <span className={own.verdictGap}>
                  {data.not_recorded.length} live meter{data.not_recorded.length === 1 ? "" : "s"}{" "}
                  {data.not_recorded.length === 1 ? "has" : "have"} no account recorded.
                </span>
              ) : (
                <span>Every live meter has an account recorded.</span>
              )}
            </p>
            </div>

            {data.accounts.length === 0 ? (
              <div className={own.pad}>
              <p className={own.formNote} data-testid="registers-none">
                No account has been recorded, so the expected-bills check cannot say who bills any meter. That is
                not a fault — it is what a workspace looks like before somebody says.
              </p>
              </div>
            ) : (
              <div className={reg.table}>
                <div className={`${reg.head} ${own.accountCols}`} aria-hidden="true">
                  <span>Vendor and account</span>
                  <span>Meters</span>
                  <span />
                </div>
                {data.accounts.map((account) => (
                  <AccountRow
                    key={account.id}
                    account={account}
                    available={[
                      ...data.not_recorded,
                      ...data.accounts
                        .filter((other) => other.id !== account.id)
                        .flatMap((other) => other.meters.filter((m) => !m.operational_to)),
                    ]}
                    mayDeclare={mayDeclare}
                    streams={streams}
                  />
                ))}
              </div>
            )}

            {data.not_recorded.length > 0 ? (
              <div data-testid="not-recorded" className={`${own.registerList} ${own.registerFlush}`}>
                <p className={own.formHead}>Not recorded</p>
                <p className={own.formNote}>
                  Nobody has said who bills these. The expected-bills check judges them on their own dates and
                  their site&rsquo;s, exactly as it did before there was a register.
                </p>
                <ul className={own.linkList}>
                  {data.not_recorded.map((meter) => (
                    <li key={meter.id} className={own.linkItem}>
                      {meterName(meter)}
                      <MeterStreams meterId={meter.id} streams={streams} />
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div className={own.pad}>
              {mayDeclare ? (
                <>
                  <RecordVendor />
                  <RecordAccount vendors={data.vendors} />
                </>
              ) : (
                <p className={own.formNote} data-testid="registers-who-can">
                  An <strong>Approver</strong> records who bills each meter. Ending an account stops
                  every stream beneath it being expected, so it is a statement about completeness
                  and not a structural edit.
                </p>
              )}
            </div>
          </>
        )}
      </Loaded>
    </Panel>
  );
}

/** One account, its meters, and the three things an Approver does to it. Its own component so each
 * account's refusal is its own: one shared mutation would put one account's error under another's
 * name and disable every other account's controls while one was saving. */
export function AccountRow({
  account,
  available,
  mayDeclare,
  streams,
}: {
  streams: readonly SourceCoverage[];
  account: SupplyAccount;
  /** Live meters that could be attached: the unrecorded ones, and those on another account. */
  available: RegisterMeter[];
  mayDeclare: boolean;
}) {
  const attach = useAttachMeters();
  const retire = useRetireAccount();
  const clear = useSetMeterAccount();
  const [mode, setMode] = useState<"attach" | "retire" | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [reason, setReason] = useState("");
  const [refusal, setRefusal] = useState<unknown>(null);
  const [message, setMessage] = useState<string | null>(null);

  function close() {
    setMode(null);
    setPicked([]);
    setEffectiveFrom("");
    setReason("");
    setRefusal(null);
  }

  async function onAttach() {
    setRefusal(null);
    try {
      await attach.mutateAsync({ accountId: account.id, meterIds: picked });
      setMessage(`${picked.length} meter${picked.length === 1 ? "" : "s"} now billed under it.`);
      close();
    } catch (error) {
      setRefusal(error);
    }
  }

  async function onRetire() {
    setRefusal(null);
    try {
      await retire.mutateAsync({ accountId: account.id, effective_from: effectiveFrom, reason });
      setMessage(
        `Ended. Streams on its ${account.meters.length} meter${
          account.meters.length === 1 ? "" : "s"
        } are not expected after ${shortDate(effectiveFrom)}. If the meters moved to a successor account, attach them to it.`,
      );
      close();
    } catch (error) {
      setRefusal(error);
    }
  }

  async function onRemove(meterId: string) {
    setRefusal(null);
    try {
      await clear.mutateAsync({ meterId, accountId: null });
    } catch (error) {
      setRefusal(error);
    }
  }

  return (
    <div
      className={`${reg.row} ${own.accountCols} ${own.accountRow}`}
      id={`account-${account.id}`}
      tabIndex={-1}
      data-testid={`account-${account.id}`}
      data-retired={account.is_retired}
    >
      <div className={own.rowHead}>
        <span className={own.rowName}>
          {account.vendor_name} · account {account.account_ref}
        </span>
        <span className={own.rowMeta}>{lifeOf(account)}</span>
      </div>
      <span>
        <span className={`${reg.pill} ${account.is_retired ? reg.pillWarn : ""}`}>
          {account.meters.length} meter{account.meters.length === 1 ? "" : "s"}
          {account.is_retired ? " · ended" : ""}
        </span>
      </span>

      {account.is_retired && account.retired_reason ? (
        <p className={own.lifeEnd}>
          Ended {shortDate(account.operational_to)} — {account.retired_reason}
        </p>
      ) : null}

      {account.meters.length === 0 ? (
        <p className={own.formNote}>No meters are billed under this account yet.</p>
      ) : (
        <ul className={`${own.linkList} ${own.meterList}`}>
          {account.meters.map((meter) => (
            <li key={meter.id} className={own.linkItem}>
              {meterName(meter)}
              {meter.operational_to ? ` · retired ${shortDate(meter.operational_to)}` : ""}
              <MeterStreams meterId={meter.id} streams={streams} />
              {mayDeclare ? (
                <>
                  {" "}
                  <button
                    type="button"
                    className={own.ownerAction}
                    aria-label={`Remove ${meterName(meter)} from this account`}
                    disabled={clear.isPending}
                    onClick={() => void onRemove(meter.id)}
                  >
                    Remove
                  </button>
                </>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {message ? (
        <p className={own.formNote} role="status">
          {message}
        </p>
      ) : null}

      {mayDeclare ? (
        <div className={own.owner}>
          <button
            type="button"
            className={own.ownerAction}
            aria-expanded={mode === "attach"}
            onClick={() => {
              setMessage(null);
              if (mode === "attach") close();
              else setMode("attach");
            }}
          >
            Attach meters
          </button>
          {account.is_retired ? null : (
            <button
              type="button"
              className={own.ownerAction}
              aria-expanded={mode === "retire"}
              onClick={() => {
                setMessage(null);
                if (mode === "retire") close();
                else setMode("retire");
              }}
            >
              Retire this account
            </button>
          )}
        </div>
      ) : null}

      {mode === "attach" ? (
        <div className={own.form} data-testid="attach-meters">
          <p className={own.formHead}>Bill these meters under this account</p>
          {available.length === 0 ? (
            <p className={own.formNote}>Every live meter is already on this account.</p>
          ) : (
            <fieldset className={own.pick}>
              <legend className={own.formNote}>
                A meter already on another account is moved, not copied.
              </legend>
              {available.map((meter) => (
                <label key={meter.id} className={own.pickItem}>
                  <input
                    type="checkbox"
                    checked={picked.includes(meter.id)}
                    onChange={(event) =>
                      setPicked(
                        event.target.checked
                          ? [...picked, meter.id]
                          : picked.filter((id) => id !== meter.id),
                      )
                    }
                  />{" "}
                  {meterName(meter)}
                </label>
              ))}
            </fieldset>
          )}
          <div className={own.buttons}>
            <button
              type="button"
              className={styles.primary}
              disabled={picked.length === 0 || attach.isPending}
              onClick={() => void onAttach()}
            >
              {attach.isPending
                ? "Attaching…"
                : `Attach ${picked.length} meter${picked.length === 1 ? "" : "s"}`}
            </button>
            <button type="button" className={own.ghost} onClick={close}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {mode === "retire" ? (
        <div className={own.form} data-testid="retire-account">
          <p className={own.formHead}>End this account</p>
          <p className={own.formNote}>
            Give the <strong>last day</strong> it billed: an account that closed on 31 March keeps
            March expected. Every stream on its meters stops being expected after that day. The
            meters stay attached — this says the account ended, not that nobody bills them.
          </p>
          <input
            type="date"
            className={own.input}
            aria-label="The last day this account billed"
            value={effectiveFrom}
            onChange={(event) => setEffectiveFrom(event.target.value)}
          />
          <input
            className={own.input}
            aria-label="Why the account is ending"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="e.g. the supply contract ended and the meters moved supplier"
          />
          <div className={own.buttons}>
            <button
              type="button"
              className={styles.primary}
              disabled={!effectiveFrom || !reason.trim() || retire.isPending}
              onClick={() => void onRetire()}
            >
              {retire.isPending ? "Ending…" : "End the account"}
            </button>
            <button type="button" className={own.ghost} onClick={close}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {/* Ending an account back-dated into a closed period (ADR-0094); the reason above is already
          the Admin's act, so this only tells everyone else whose it is. */}
      <PostCloseRetry error={refusal} />
      <WriteFailed
        error={isPeriodClosed(refusal) ? null : refusal}
        of="this account"
        onReload={() => setRefusal(null)}
      />
    </div>
  );
}

function RecordVendor() {
  const record = useRecordVendor();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [refusal, setRefusal] = useState<unknown>(null);

  if (!open) {
    return (
      <button
        type="button"
        className={own.declareOpen}
        data-testid="record-vendor-open"
        onClick={() => setOpen(true)}
      >
        Record a vendor
      </button>
    );
  }

  return (
    <form
      className={own.form}
      data-testid="record-vendor"
      onSubmit={(event) => {
        event.preventDefault();
        setRefusal(null);
        record.mutateAsync({ name: name.trim() }).then(
          () => {
            setName("");
            setOpen(false);
          },
          (error: unknown) => setRefusal(error),
        );
      }}
    >
      <p className={own.formHead}>Who bills you?</p>
      <p className={own.formNote}>
        The company&rsquo;s name as it prints it on a bill. This is your own note, not a supplier
        asked for data — and there is nowhere to put a person&rsquo;s name, because a contact is
        not something this register keeps.
      </p>
      <input
        className={own.input}
        aria-label="Vendor name"
        value={name}
        required
        maxLength={200}
        onChange={(event) => setName(event.target.value)}
      />
      <div className={own.buttons}>
        <button
          type="submit"
          className={styles.primary}
          disabled={!name.trim() || record.isPending}
        >
          {record.isPending ? "Recording…" : "Record the vendor"}
        </button>
        <button
          type="button"
          className={own.ghost}
          onClick={() => {
            setOpen(false);
            setName("");
            setRefusal(null);
          }}
        >
          Cancel
        </button>
      </div>
      <WriteFailed error={refusal} of="the vendor register" onReload={() => setRefusal(null)} />
    </form>
  );
}

function RecordAccount({
  vendors,
}: {
  vendors: Array<{ id: string; name: string; account_count: number }>;
}) {
  const record = useRecordAccount();
  const [open, setOpen] = useState(false);
  const [vendorId, setVendorId] = useState("");
  const [reference, setReference] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [refusal, setRefusal] = useState<unknown>(null);

  function cancel() {
    setOpen(false);
    setVendorId("");
    setReference("");
    setFrom("");
    setTo("");
    setRefusal(null);
  }

  if (!open) {
    return (
      <button
        type="button"
        className={own.declareOpen}
        data-testid="record-account-open"
        onClick={() => setOpen(true)}
      >
        Record an account
      </button>
    );
  }

  if (vendors.length === 0) {
    return (
      <p className={own.formNote} data-testid="record-account-needs-a-vendor">
        Record a vendor first — an account is billed by one.
      </p>
    );
  }

  return (
    <form
      className={own.declare}
      data-testid="record-account"
      onSubmit={(event) => {
        event.preventDefault();
        setRefusal(null);
        record
          .mutateAsync({
            vendor_id: vendorId || (vendors[0]?.id ?? ""),
            account_ref: reference.trim(),
            operational_from: from || null,
            operational_to: to || null,
          })
          .then(cancel, (error: unknown) => setRefusal(error));
      }}
    >
      <label className={own.field}>
        <span className={own.fieldLabel}>Vendor</span>
        <select
          className={own.input}
          value={vendorId || (vendors[0]?.id ?? "")}
          onChange={(event) => setVendorId(event.target.value)}
        >
          {vendors.map((vendor) => (
            <option key={vendor.id} value={vendor.id}>
              {vendor.name}
            </option>
          ))}
        </select>
      </label>
      <label className={own.field}>
        <span className={own.fieldLabel}>Account reference</span>
        <input
          className={own.input}
          value={reference}
          required
          maxLength={255}
          onChange={(event) => setReference(event.target.value)}
        />
        <span className={own.formNote}>
          As the vendor prints it. It is kept as typed and never matched against a bill.
        </span>
      </label>
      <label className={own.field}>
        <span className={own.fieldLabel}>Began (optional)</span>
        <input
          type="date"
          className={own.input}
          value={from}
          onChange={(event) => setFrom(event.target.value)}
        />
        <span className={own.formNote}>
          A start date stops anything before it being expected, for <strong>every</strong> meter on
          the account. Leave it blank unless the account really began then.
        </span>
      </label>
      <label className={own.field}>
        <span className={own.fieldLabel}>Ended (optional)</span>
        <input
          type="date"
          className={own.input}
          value={to}
          onChange={(event) => setTo(event.target.value)}
        />
      </label>
      <div className={own.declareActions}>
        <button
          type="submit"
          className={own.declareSubmit}
          disabled={!reference.trim() || record.isPending}
        >
          {record.isPending ? "Recording…" : "Record the account"}
        </button>
        <button type="button" className={own.declareCancel} onClick={cancel}>
          Cancel
        </button>
      </div>
      <WriteFailed
        error={refusal}
        of="the register"
        testId="record-account-error"
        className={own.declareError}
        onReload={() => setRefusal(null)}
      />
    </form>
  );
}
